import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CourseFormDialog from "@/components/CourseFormDialog";
import ModuleFormDialog from "@/components/ModuleFormDialog";
import CourseMaterials from "@/components/CourseMaterials";
import CourseExercises from "@/components/exercises/CourseExercises";
import { ArrowLeft, Pencil, Plus, Layers, BookOpen, Trash2, User, Clock, Award } from "lucide-react";
import { toast } from "sonner";
import { YEAR_LABEL, courseYears, yearsLabel } from "@/lib/school";
import { posterUrl } from "@/lib/seminars";
import { useAuth, isAdminOrSegreteria, isTeacher } from "@/lib/auth";

export default function CourseDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [editModule, setEditModule] = useState(null);
  const [delModule, setDelModule] = useState(null);

  const { data: course } = useQuery({
    queryKey: ["course", id],
    queryFn: async () => (await api.get(`/courses/${id}`)).data,
  });

  // canManageMaterials: admin/segreteria sempre; docente se titolare
  const canManageMaterials = isAdminOrSegreteria(user) || (
    isTeacher(user) && course && (course.teacher_ids || []).includes(user.teacher_id)
  );

  const canEditCourse = ["admin", "direzione", "segreteria"].includes(user?.role);

  const editMut = useMutation({
    mutationFn: async (p) => (await api.patch(`/courses/${id}`, p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["course", id] }); toast.success("Corso aggiornato"); setEditOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const createModMut = useMutation({
    mutationFn: async (p) => (await api.post(`/courses/${id}/modules`, p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["course", id] }); toast.success("Modulo creato"); setModuleOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const editModMut = useMutation({
    mutationFn: async ({ mid, p }) => (await api.patch(`/courses/modules/${mid}`, p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["course", id] }); toast.success("Modulo aggiornato"); setEditModule(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delModMut = useMutation({
    mutationFn: async (mid) => (await api.delete(`/courses/modules/${mid}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["course", id] }); toast.success("Modulo eliminato"); setDelModule(null); },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelModule(null); },
  });

  if (!course) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const totalModuleCfu = (course.modules || []).reduce((s, m) => s + (m.cfu_partial || 0), 0);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to={course.track_type === "modulare" ? "/modulare/corsi" : "/scuola/corsi"} data-testid="course-back-link" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna al catalogo
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div className="min-w-0 flex items-start gap-4">
          {course.is_seminar && course.poster && (
            <a href={posterUrl(course)} target="_blank" rel="noreferrer" className="shrink-0" data-testid="course-seminar-poster">
              <img src={posterUrl(course)} alt={`Locandina ${course.name}`} className="w-28 aspect-[4/5] object-cover rounded-lg border border-stone-200 shadow-sm"/>
            </a>
          )}
          <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500">
            {course.area_name && (
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: course.area_color || "#1C352D" }}/>
                {course.area_name}
              </span>
            )}
            {courseYears(course).length > 0 && <> · {yearsLabel(course)}</>}
            {course.academic_year && <> · {course.academic_year}</>}
            {course.is_seminar && <Badge className="bg-[#E2725B] text-white text-[9px] rounded-full ml-2">{course.seminar_kind === "workshop" ? "WORKSHOP" : "SEMINARIO"}</Badge>}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1C352D] mt-1">{course.name}</h1>
          <div className="text-sm text-stone-600 mt-1">{course.code && <span className="mono">{course.code} · </span>}{course.description || "Nessuna descrizione."}</div>
          </div>
        </div>
        {canEditCourse && <Button
          variant="outline"
          onClick={() => setEditOpen(true)}
          data-testid="course-edit-btn"
          className="rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white"
        >
          <Pencil size={14} className="mr-1.5"/> Modifica corso
        </Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Award size={11}/> CFU</div>
          <div className="text-2xl font-semibold text-[#E2725B] mt-1">{course.cfu ?? "—"}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Clock size={11}/> Ore</div>
          <div className="text-2xl font-semibold mt-1">{course.hours ?? "—"}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Layers size={11}/> Moduli</div>
          <div className="text-2xl font-semibold mt-1">{course.modules?.length || 0}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><User size={11}/> Docenti</div>
          <div className="text-2xl font-semibold mt-1">{course.teachers?.length || 0}</div>
        </Card>
      </div>

      {course.teachers?.length > 0 && (
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none mb-6">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Docenti co-titolari del corso</div>
          <div className="flex flex-wrap gap-2">
            {course.teachers.map((t) => (
              <Link key={t.id} to={`/scuola/docenti/${t.id}`}>
                <Badge className="bg-stone-100 text-stone-800 hover:bg-[#1C352D] hover:text-white rounded-full text-xs font-normal transition-colors cursor-pointer">
                  {t.name}
                </Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-5 border-b border-stone-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium flex items-center gap-2"><Layers size={14}/> Moduli del corso</div>
            <div className="text-xs text-stone-500 mt-0.5">
              {course.modules?.length || 0} moduli · {totalModuleCfu} CFU parziali cumulativi
            </div>
          </div>
          {canEditCourse && <Button
            onClick={() => setModuleOpen(true)}
            data-testid="module-add-btn"
            className="rounded-full bg-[#E2725B] hover:bg-[#c85d47]"
          >
            <Plus size={14} className="mr-1.5"/> Aggiungi modulo
          </Button>}
        </div>
        {!course.modules || course.modules.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">
            Nessun modulo. Aggiungi il primo per suddividere il corso (es. &quot;La Persona di Dio&quot; in Sistematica 1).
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {course.modules.map((m, i) => (
              <li key={m.id} data-testid={`module-${m.id}`} className="p-5 flex items-start gap-4 animate-in-row" style={{ animationDelay: `${i * 20}ms` }}>
                <div className="w-9 h-9 rounded-lg bg-[#1C352D] text-white grid place-items-center text-sm shrink-0">
                  {m.order || i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{m.name}</div>
                    {m.cfu_partial != null && <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">{m.cfu_partial} CFU</Badge>}
                    {m.hours != null && <Badge className="bg-stone-100 text-stone-700 text-[10px] rounded-full">{m.hours}h</Badge>}
                  </div>
                  {m.description && <div className="text-sm text-stone-600 mt-1">{m.description}</div>}
                  {m.teachers?.length > 0 && (
                    <div className="text-[11px] text-stone-500 mt-1.5">
                      Docenti: {m.teachers.map((t) => t.name).join(", ")}
                    </div>
                  )}
                </div>
                {canEditCourse && <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditModule(m)}
                    className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-[#1C352D]"
                    data-testid={`module-edit-${m.id}`}
                  >
                    <Pencil size={14}/>
                  </button>
                  <button
                    onClick={() => setDelModule(m)}
                    className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-700"
                    data-testid={`module-del-${m.id}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="mt-6">
        <CourseMaterials courseId={id} canManage={canManageMaterials} />
      </div>

      <div className="mt-6">
        <CourseExercises courseId={id} canManage={canManageMaterials} />
      </div>

      <CourseFormDialog
        open={editOpen} onOpenChange={setEditOpen} mode="edit" initial={course}
        submitting={editMut.isPending}
        onSubmit={(p) => editMut.mutate(p)}
      />
      <ModuleFormDialog
        open={moduleOpen} onOpenChange={setModuleOpen} mode="create"
        submitting={createModMut.isPending}
        onSubmit={(p) => createModMut.mutate(p)}
      />
      <ModuleFormDialog
        open={!!editModule} onOpenChange={(v) => !v && setEditModule(null)} mode="edit" initial={editModule}
        submitting={editModMut.isPending}
        onSubmit={(p) => editModMut.mutate({ mid: editModule.id, p })}
      />

      <AlertDialog open={!!delModule} onOpenChange={(v) => !v && setDelModule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il modulo &laquo;{delModule?.name}&raquo;?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione è irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => delModule && delModMut.mutate(delModule.id)}>
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
